import { useState } from "react";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import api from "@/lib/api";
import { ONET_ATTRIBUTION, ONET_URL } from "@/lib/riasec";
import { OnetBadge } from "@/components/OnetBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ExternalLink, Loader2, Compass, AlertTriangle } from "lucide-react";

export default function CareerExplorer() {
  const [keyword, setKeyword] = useState("");
  const [state, setState] = useState({ status: "idle" }); // idle|loading|done|unavailable|error
  const [careers, setCareers] = useState([]);
  const [lastKw, setLastKw] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    setState({ status: "loading" });
    setLastKw(kw);
    try {
      const { data } = await api.get("/career-explorer", { params: { keyword: kw } });
      if (data.available === false) { setState({ status: "unavailable" }); return; }
      setCareers(data.careers || []);
      setState({ status: "done" });
    } catch {
      setState({ status: "error" });
    }
  };

  return (
    <MarketingPage
      eyebrow="Explore Careers"
      title="Browse real occupations, powered by O*NET."
      lead="Search the U.S. Department of Labor's O*NET occupation database. Students can go deeper by taking the Career Interest Profiler in their dashboard to see which of these fit their interests."
    >
      <form onSubmit={search} className="flex flex-col gap-3 sm:flex-row" data-testid="career-explorer-form">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Try “nurse”, “software”, “welding”, “design”…"
            className="pl-9"
            data-testid="career-explorer-input"
          />
        </div>
        <Button type="submit" className="rounded-lg bg-[#003057] font-semibold text-white hover:bg-[#00243f]" data-testid="career-explorer-search-btn">
          {state.status === "loading" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Search
        </Button>
      </form>

      <div className="mt-8">
        {state.status === "idle" && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="career-explorer-idle">
            <Compass className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm">Search a keyword above to explore matching occupations.</p>
          </div>
        )}

        {state.status === "unavailable" && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="career-explorer-unavailable">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>Career search is temporarily unavailable. Please check back later.</span>
          </div>
        )}

        {state.status === "error" && (
          <p className="text-sm text-red-600" data-testid="career-explorer-error">Something went wrong. Please try again.</p>
        )}

        {state.status === "done" && (
          <>
            {careers.length === 0 ? (
              <p className="rounded-2xl border border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="career-explorer-empty">
                No occupations found for “{lastKw}”. Try a different keyword.
              </p>
            ) : (
              <>
                <p className="mb-4 text-sm font-semibold text-slate-500" data-testid="career-explorer-count">{careers.length} occupations for “{lastKw}”</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="career-explorer-results">
                  {careers.map((c) => (
                    <div key={c.code} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 card-elevate">
                      <div>
                        <p className="font-heading text-sm font-bold text-[#003057]">{c.title}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {c.bright_outlook && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Bright Outlook</span>}
                          {c.green && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Green</span>}
                        </div>
                      </div>
                      {c.code && (
                        <a href={`https://www.onetonline.org/link/summary/${c.code}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#005291] hover:underline" data-testid={`explorer-link-${c.code}`}>
                          Details <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="mt-10 rounded-lg bg-slate-50 px-4 py-3" data-testid="onet-attribution">
        <OnetBadge className="mb-2" />
        <p className="text-[11px] leading-relaxed text-slate-500">
        {ONET_ATTRIBUTION}{" "}
        <a href={ONET_URL} target="_blank" rel="noreferrer" className="font-semibold text-[#005291] underline hover:text-[#78be21]">O*NET Web Services</a>.
        </p>
      </div>
    </MarketingPage>
  );
}
